/* eslint-disable max-len */
import React, { FunctionComponent, useEffect, useState } from 'react';

import { Col, Heading, Link, Product, Row } from '@savebetter/components';
import { ColWidth } from '@savebetter/components/src/components/shared/Grid/Col/Col.types';

import { postLoginUrl } from '../../../config';
import { useBankProducts } from '../context/BankProductsContext';
import { useDatasources } from '../context/DataSourceContext';
import { getProductIdFromVariable, productIdVarRegExp, replaceCustomVars } from '../utils/replaceCustomVar';

import { MediaProps } from './Media';
import { PageSectionBlok, PageSectionProps } from './PageSection';
import { Section } from './Section';

const colStyles = 'p-4 md:p-4 lg:p-4 font-normal text-left min-w-24';
const rowStyles = 'border-black-60 border-b-1 min-w-144 flex-nowrap';

interface Blok extends PageSectionBlok {
  media: MediaProps[];
  heading: string;
  text: string;
  buttonLabel: string;
  inputLabel: string;
  inputPlaceholder: string;
  tableGrid: Table;
}

interface HeadTitle {
  value: string;
}

interface TableProps extends PageSectionProps {
  blok: Blok;
}

interface ColType {
  [key: string]: string;
  value: string;
  _uid: string;
}

interface RowType {
  [key: string]: string | ColType[];
  body: ColType[];
  _uid: string;
}

interface Table {
  [key: string]: HeadTitle[] | RowType[] | string;
  thead: HeadTitle[];
  tbody: RowType[];
}

interface LinkComponentProps {
  row: RowType;
  col: ColType;
  product?: Product;
  linkIndex?: number;
  targetIndex?: number;
}

interface RowGridProps {
  isProductIdTable: boolean;
  row: RowType;
  tableLength: number;
  linkIndex: number;
  targetIndex: number;
  linkNameIndex: number;
  colWidth: ColWidth;
  productProps: string[];
  product?: Product;
}

const DEFAULT_VALUE = '--';

// check if table cell values are manually inserted or should be generated by productIds
const isProductIdTableGrid = (tbody: RowType[]): boolean => {
  const tbodyFirstCellValue = tbody?.[0].body?.[0].value;
  return productIdVarRegExp.test(tbodyFirstCellValue);
};

const getHeadCell = (tableGrid: Table, headValue: string) =>
  tableGrid?.thead?.findIndex(({ value }) => value.toLowerCase() === headValue.toLowerCase());

const getColValue = (colIndex: number, productProps: string[], product?: Product): string => {
  if (!product) {
    return DEFAULT_VALUE;
  }
  const productPropName = productProps[colIndex];
  const productObj = product as unknown as { [key: string]: string };
  return productPropName in product ? productObj[productPropName] : DEFAULT_VALUE;
};

const productMappedProps: { [key: string]: string } = {
  'financial-institution': 'bankName',
  'savings-product': 'productName',
  apy: 'annualPercentageYield',
  term: 'maturity',
  type: 'productType',
  linkname: 'linkName'
};

const getTableHeadings = (thead: HeadTitle[]): string[] =>
  thead
    ? thead.reduce(
        (acc: string[], { value }): string[] => (value !== 'linkTarget' && value !== 'link' ? [...acc, value] : acc),
        []
      )
    : [];

// convert table head titles into product prop names
const getProductPropsFromTableHeadings = (tableHeadings?: string[]): string[] =>
  tableHeadings
    ? tableHeadings.reduce((acc: string[], value: string): string[] => {
        const key = value.toLowerCase().replaceAll(' ', '-');
        return key in productMappedProps ? [...acc, productMappedProps[key]] : acc;
      }, [])
    : [];

const LinkComponent: FunctionComponent<LinkComponentProps> = ({ row, col, linkIndex, targetIndex, product }) => {
  if (product) {
    return <Link href={`${postLoginUrl}/auth/register?productId=${product.id}`}>Save Now</Link>;
  } else if (targetIndex && linkIndex) {
    return (
      <Link
        href={linkIndex >= 0 ? row.body[linkIndex].value : '/'}
        target={targetIndex >= 0 ? row.body[targetIndex].value : ''}
      >
        {col.value}
      </Link>
    );
  }
  /*
    Roger's note: Note that not all products will return a Maturity. Savings (vs Term) products do not have this value, hence the blank space in the example image.
  */
  return <span>{DEFAULT_VALUE}</span>;
};

const RowGrid: FunctionComponent<RowGridProps> = ({
  isProductIdTable,
  row,
  linkIndex,
  targetIndex,
  linkNameIndex,
  colWidth,
  product,
  productProps
}) => {
  const { contextProducts: products } = useBankProducts();
  const { contextDatasources: datasources } = useDatasources();
  return (
    <Row gutter={0} className={rowStyles} key={row._uid}>
      {row.body.map((col: ColType, index: number) => {
        // link and target cols should not be rendered
        if (index === linkIndex || index === targetIndex) {
          return null;
        }
        const colValue = isProductIdTable ? getColValue(index, productProps, product) : col.value;

        return (
          <Col width={colWidth} className={`${colStyles} self-start`} key={col._uid}>
            {index === linkNameIndex ? (
              <LinkComponent row={row} col={col} linkIndex={linkIndex} targetIndex={targetIndex} product={product} />
            ) : (
              replaceCustomVars(colValue, products, datasources)
            )}
          </Col>
        );
      })}
    </Row>
  );
};

export const TableGrid: FunctionComponent<TableProps> = ({ blok, sectionClassName }) => {
  const [isAutomaticTable, setIsAutomaticTable] = useState<boolean>(false);
  const { contextProducts: products } = useBankProducts();
  const { hasBackground, hasBorder, title, tableGrid, hasPaddingTop, hasPaddingBottom, columnsLength } = blok;
  const tableHeadings = getTableHeadings(tableGrid?.thead);
  const productProps = getProductPropsFromTableHeadings(tableHeadings);

  const linkIndex = getHeadCell(tableGrid, 'link');
  const targetIndex = getHeadCell(tableGrid, 'linkTarget');
  const linkNameIndex = getHeadCell(tableGrid, 'linkName');
  const tableLength = productProps.filter(Boolean).length;

  const colWidth = (columnsLength ? `1/${columnsLength}` : `1/${tableLength}`) as ColWidth;

  useEffect(() => {
    const isProductIdsTable = isProductIdTableGrid(tableGrid?.tbody);
    if (!isAutomaticTable && isProductIdsTable) {
      setIsAutomaticTable(isProductIdsTable);
    }
  }, [isAutomaticTable, tableGrid?.tbody]);

  return (
    <Section
      width="md"
      hasBackground={hasBackground}
      hasBorder={hasBorder}
      className={sectionClassName}
      blok={blok}
      hasPaddingBottom={hasPaddingBottom}
      hasPaddingTop={hasPaddingTop}
    >
      {title && (
        <Heading className="text-center lg:pb-16 pb-8" kind="2" level="3">
          {title}
        </Heading>
      )}
      <Section className="overflow-x-auto">
        <Row gutter={0} className={rowStyles}>
          {tableHeadings.map(tableHeading => {
            const key = tableHeading.toLowerCase().replaceAll(' ', '-');
            return (
              <Col width={colWidth} className={`${colStyles} self-end`} key={key}>
                {tableHeading !== 'linkName' && <span className="font-bold">{tableHeading}</span>}
              </Col>
            );
          })}
        </Row>
        {tableGrid?.tbody?.map((row: RowType, index: number) => {
          // removing curly braces from customVarId and gather productId
          const productId = getProductIdFromVariable(tableGrid?.tbody?.[index].body?.[0].value);
          const product = products?.find(p => p.id === productId);
          return (
            <RowGrid
              key={row._uid}
              isProductIdTable={isAutomaticTable}
              row={row}
              tableLength={tableLength}
              linkIndex={linkIndex}
              targetIndex={targetIndex}
              linkNameIndex={linkNameIndex}
              colWidth={colWidth}
              productProps={productProps}
              product={product}
            />
          );
        })}
      </Section>
    </Section>
  );
};
